import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { LanguageSwitcher } from './LanguageSwitcher'

export function InformationPage() {
  const { t, i18n } = useTranslation()

  useEffect(() => {
    const previousTitle = document.title
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]')
    const previousDescription = description?.content
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    const previousCanonical = canonical?.href
    const openGraphUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]')
    const previousOpenGraphUrl = openGraphUrl?.content
    const informationUrl = 'https://arubaanimalradar.com/information'
    document.title = t('info.metaTitle')
    if (description) description.content = t('info.metaDescription')
    if (canonical) canonical.href = informationUrl
    if (openGraphUrl) openGraphUrl.content = informationUrl

    return () => {
      document.title = previousTitle
      if (description && previousDescription !== undefined) {
        description.content = previousDescription
      }
      if (canonical && previousCanonical) canonical.href = previousCanonical
      if (openGraphUrl && previousOpenGraphUrl !== undefined) {
        openGraphUrl.content = previousOpenGraphUrl
      }
    }
  }, [i18n.resolvedLanguage, t])

  return (
    <div className="info-shell">
      <header className="info-header">
        <Link className="info-brand" to="/">
          <img src="/brand/logo-header.webp" alt="" width="48" height="48" />
          <span className="info-brand-text">
            Aruba <em>Animal</em> Radar
          </span>
        </Link>
        <div className="info-header-actions">
          <Link className="btn" to="/privacy">
            {t('legal.privacy')}
          </Link>
          <Link className="btn" to="/terms">
            {t('legal.terms')}
          </Link>
          <LanguageSwitcher />
          <Link className="btn btn-primary" to="/">
            {t('info.openMap')}
          </Link>
        </div>
      </header>

      <main className="info-page">
        <section className="info-hero">
          <div className="info-kicker">{t('info.kicker')}</div>
          <h1>{t('info.title')}</h1>
          <p>{t('info.intro')}</p>
          <Link className="btn btn-primary info-hero-action" to="/">
            {t('info.openMap')}
          </Link>
        </section>

        <section className="info-section" aria-labelledby="info-purpose">
          <div className="info-section-number">01</div>
          <div>
            <h2 id="info-purpose">{t('info.purposeTitle')}</h2>
            <p>{t('info.purposeBody')}</p>
          </div>
        </section>

        <section className="info-section" aria-labelledby="info-features">
          <div className="info-section-number">02</div>
          <div>
            <h2 id="info-features">{t('info.featuresTitle')}</h2>
            <ul className="info-feature-list">
              <li>{t('info.features.report')}</li>
              <li>{t('info.features.map')}</li>
              <li>{t('info.features.history')}</li>
              <li>{t('info.features.search')}</li>
              <li>{t('info.features.lost')}</li>
            </ul>
          </div>
        </section>

        <section className="info-section" aria-labelledby="info-reports">
          <div className="info-section-number">03</div>
          <div>
            <h2 id="info-reports">{t('info.reportsTitle')}</h2>
            <p>{t('info.reportsBody')}</p>
          </div>
        </section>

        <section className="info-section" aria-labelledby="info-languages">
          <div className="info-section-number">04</div>
          <div>
            <h2 id="info-languages">{t('info.languagesTitle')}</h2>
            <p>{t('info.languagesBody')}</p>
          </div>
        </section>

        <section className="info-section" aria-labelledby="info-privacy">
          <div className="info-section-number">05</div>
          <div>
            <h2 id="info-privacy">{t('info.privacyTitle')}</h2>
            <p>{t('info.privacyBody')}</p>
            <Link to="/privacy">{t('info.privacyLink')}</Link>
          </div>
        </section>

        <section className="info-beta" aria-labelledby="info-beta">
          <h2 id="info-beta">{t('info.betaTitle')}</h2>
          <p>{t('info.betaBody')}</p>
          <a href="mailto:info@arubaanimalradar.com">info@arubaanimalradar.com</a>
        </section>
      </main>

      <footer className="info-footer">
        <span>Aruba Animal Radar</span>
        <nav aria-label={t('legal.navigation')}>
          <Link to="/privacy">{t('legal.privacy')}</Link>
          <Link to="/terms">{t('legal.terms')}</Link>
          <Link to="/">{t('info.openMap')}</Link>
        </nav>
      </footer>
    </div>
  )
}
